import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CircleHelp } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import Select from 'src/components/input/select/Select';
import type { IProperty } from 'src/services/api/properties/types';
import {
    type ITouringSpecialDay,
    type TouringDay,
    useClearTouringHours,
    useProperties,
    useSetTouringHours,
    useTouringHours,
} from 'src/services/api';
import { FALLBACK_TIMEZONES, getTimezoneAbbr, getUserTimezone } from 'src/utils/format-date-time';
import SpecialDaysSection from 'src/components/property-touring-hours/SpecialDaysSection';
import WeeklyHoursSchedule from 'src/components/property-touring-hours/WeeklyHoursSchedule';
import {
    type DayForm,
    type SpecialDayForm,
    type WindowForm,
    allDayWeekly,
    buildPayload,
    buildWeeklyFromPeriods,
    defaultWindow,
    emptyWeekly,
    getDirtyKey,
    getValidationError,
} from 'src/components/property-touring-hours/touring-hours-form';
import classes from 'src/components/property-touring-hours/PropertyTouringHoursSection.module.css';
import { useNotifications } from 'src/notifications';

export interface PropertyTouringHoursSectionRef {
    applyChanges: () => Promise<boolean>;
    isDirty: () => boolean;
}

const PropertyTouringHoursSection = forwardRef<PropertyTouringHoursSectionRef, { property: IProperty }>(
    function PropertyTouringHoursSection({ property }, ref) {
        const { data: touringHours } = useTouringHours(property.id);
        const { data: properties = [] } = useProperties();
        const setTouringHours = useSetTouringHours(property.id);
        const clearTouringHours = useClearTouringHours(property.id);
        const { addNotification } = useNotifications();

        const [timezone, setTimezone] = useState(getUserTimezone());
        const [maxConcurrentTours, setMaxConcurrentTours] = useState('1');
        const [weekly, setWeekly] = useState<Record<TouringDay, DayForm>>(emptyWeekly);
        const [specialDays, setSpecialDays] = useState<SpecialDayForm[]>([]);
        const [isEnabled, setEnabled] = useState(false);
        const [saveError, setSaveError] = useState<string | null>(null);
        const [savedKey, setSavedKey] = useState('');
        const [savedEnabled, setSavedEnabled] = useState(false);
        const [isSaving, setIsSaving] = useState(false);

        const parentProperty = properties.find((candidate) => candidate.id === property.parent_property_id);
        const childCount = properties.filter((candidate) => candidate.parent_property_id === property.id).length;
        const isReadOnly = touringHours ? !touringHours.can_set_own_hours || touringHours.is_inherited : false;
        const isConfigured = Boolean(touringHours?.is_set);
        const currentKey = getDirtyKey(timezone, maxConcurrentTours, weekly, specialDays);
        const hasUnsavedChanges = isEnabled !== savedEnabled || (isEnabled && currentKey !== savedKey);
        const validationError = isEnabled ?
            getValidationError(timezone, maxConcurrentTours, weekly, specialDays) :
            null;

        useEffect(() => {
            if (!touringHours) {
                return;
            }
            const nextTimezone = touringHours.timezone || getUserTimezone();
            const nextMaxConcurrentTours = touringHours.max_concurrent_tours?.toString() ?? '1';
            const nextWeekly = touringHours.is_set ? buildWeeklyFromPeriods(touringHours.periods) : emptyWeekly();
            const nextSpecialDays = touringHours.special_days.map((specialDay: ITouringSpecialDay) => ({
                date: specialDay.date,
                is_closed: specialDay.is_closed,
                windows: specialDay.windows,
            }));
            setTimezone(nextTimezone);
            setMaxConcurrentTours(nextMaxConcurrentTours);
            setWeekly(nextWeekly);
            setSpecialDays(nextSpecialDays);
            setEnabled(touringHours.is_set);
            setSavedEnabled(touringHours.is_set);
            setSaveError(null);
            setSavedKey(getDirtyKey(nextTimezone, nextMaxConcurrentTours, nextWeekly, nextSpecialDays));
        }, [touringHours]);

        const applyChanges = useCallback(async () => {
            setSaveError(null);
            if (!hasUnsavedChanges) {
                addNotification('Tour availability schedule is already up to date', 'success');
                return true;
            }
            setIsSaving(true);
            try {
                if (!isEnabled) {
                    if (!isConfigured) {
                        setSavedEnabled(false);
                        return true;
                    }
                    try {
                        await clearTouringHours.mutateAsync();
                        setSavedEnabled(false);
                        addNotification('Tour availability schedule turned off', 'success');
                        return true;
                    } catch {
                        const errorMessage = 'Failed to turn off the tour availability schedule.';
                        setSaveError(errorMessage);
                        addNotification(errorMessage, 'error');
                        return false;
                    }
                }
                if (validationError) {
                    setSaveError(validationError);
                    addNotification(validationError, 'error');
                    return false;
                }
                try {
                    const payload = buildPayload(timezone, maxConcurrentTours, weekly, specialDays);
                    await setTouringHours.mutateAsync(payload);
                    setSavedKey(JSON.stringify(payload));
                    setSavedEnabled(true);
                    addNotification('Tour availability schedule saved', 'success');
                    return true;
                } catch {
                    const errorMessage = 'Failed to save tour availability schedule.';
                    setSaveError(errorMessage);
                    addNotification(errorMessage, 'error');
                    return false;
                }
            } finally {
                setIsSaving(false);
            }
        }, [addNotification, clearTouringHours, hasUnsavedChanges, isConfigured, isEnabled, maxConcurrentTours, setTouringHours, specialDays, timezone, validationError, weekly]);

        useImperativeHandle(ref, () => ({
            applyChanges,
            isDirty: () => hasUnsavedChanges,
        }), [applyChanges, hasUnsavedChanges]);

        const updateWindow = (day: TouringDay, index: number, update: Partial<WindowForm>) => {
            setWeekly((current) => ({
                ...current,
                [day]: {
                    ...current[day],
                    windows: current[day].windows.map((window, i) => i === index ? { ...window, ...update } : window),
                },
            }));
        };

        const addWindow = (day: TouringDay) => {
            setWeekly((current) => ({
                ...current,
                [day]: { ...current[day], windows: [...current[day].windows, defaultWindow()] },
            }));
        };

        const removeWindow = (day: TouringDay, index: number) => {
            setWeekly((current) => ({
                ...current,
                [day]: {
                    ...current[day],
                    windows: current[day].windows.filter((_, i) => i !== index),
                },
            }));
        };

        const toggleDay = (day: TouringDay, isOpen: boolean) => {
            setWeekly((current) => ({
                ...current,
                [day]: {
                    windows: isOpen ?
                        (current[day].windows.length > 0 ? current[day].windows : [defaultWindow()]) :
                        [],
                },
            }));
        };

        const updateSpecialDay = (index: number, update: (day: SpecialDayForm) => SpecialDayForm) => {
            setSpecialDays((current) => current.map((day, i) => i === index ? update(day) : day));
        };

        const toggleSpecialDay = (index: number, isOpen: boolean) => {
            updateSpecialDay(index, (day) => isOpen ?
                { ...day, is_closed: false, windows: day.windows.length > 0 ? day.windows : [defaultWindow()] } :
                { ...day, is_closed: true, windows: [] });
        };

        const deleteSpecialDay = (index: number) => {
            setSpecialDays((current) => current.filter((_, i) => i !== index));
        };

        const addSpecialDay = (date: string) => {
            setSpecialDays((current) => [
                ...current,
                { date, is_closed: false, windows: [defaultWindow()] },
            ]);
        };

        const toggleEnabled = (checked: boolean) => {
            setSaveError(null);
            setEnabled(checked);
            if (checked && !isConfigured) {
                setWeekly(allDayWeekly());
                setSpecialDays([]);
                setTimezone(touringHours?.timezone || getUserTimezone());
                setMaxConcurrentTours(touringHours?.max_concurrent_tours?.toString() ?? '1');
            }
        };

        return (
            <form className={classes.root} onSubmit={(event) => event.preventDefault()}>
                <FlexColumn rowGap={2}>
                    <div className={classes.header}>
                        <div className={classes.title}>
                            <Typography variant="body2" fontWeight={500} className={classes.titleText}>Tour Availability Schedule</Typography>
                            <Tooltip title="Tour availability defines the regular weekly and date-specific hours used to guide tour scheduling. It warns when tours are booked outside available times.">
                                <IconButton className={classes.helpIcon} size="small" data-testid="tour-availability-help-button">
                                    <CircleHelp size={16} />
                                </IconButton>
                            </Tooltip>
                            <Switch
                                checked={isEnabled}
                                onChange={(_, checked) => toggleEnabled(checked)}
                                size="small"
                                disabled={isReadOnly}
                                inputProps={{ 'aria-label': 'Enable tour availability schedule' }}
                                data-testid="toggle-tour-availability-schedule-switch"
                            />
                        </div>
                        {hasUnsavedChanges && (
                            <Typography variant="caption" color="text.secondary">
                                Unsaved changes
                            </Typography>
                        )}
                    </div>

                    {isReadOnly && (
                        <Alert severity="info" className={classes.notice}>
                            {touringHours?.is_inherited ?
                                `This schedule is inherited from ${parentProperty?.name || 'its parent property'}. You can only change the parent property's schedule.` :
                                'This property type cannot set its own schedule. You can only change its parent property.'}
                        </Alert>
                    )}

                    {!isReadOnly && isConfigured && (
                        <Alert severity="info" className={classes.notice}>
                            Changing this schedule will change availability for this property{childCount > 0 ? ` and ${childCount} child ${childCount === 1 ? 'property' : 'properties'}` : ''}.
                        </Alert>
                    )}

                    {saveError && <Alert severity="error">{saveError}</Alert>}

                    {isEnabled && (
                        <>
                            <div className={classes.settings}>
                                <Select
                                    label="Timezone"
                                    size="small"
                                    value={timezone}
                                    onChange={setTimezone}
                                    options={FALLBACK_TIMEZONES.map((option) => ({
                                        value: option,
                                        label: <Typography component="span" variant="body2">{option}</Typography>,
                                    }))}
                                    helperText={timezone ? getTimezoneAbbr(timezone) : undefined}
                                    disabled={isReadOnly}
                                    fullWidth
                                    data-testid="tour-availability-timezone-select"
                                />
                                <TextField
                                    label="Max concurrent tours"
                                    type="number"
                                    size="small"
                                    value={maxConcurrentTours}
                                    onChange={(event) => setMaxConcurrentTours(event.target.value)}
                                    inputProps={{ min: 1 }}
                                    disabled={isReadOnly}
                                />
                            </div>

                            <WeeklyHoursSchedule
                                weekly={weekly}
                                isReadOnly={isReadOnly}
                                onToggleDay={toggleDay}
                                onUpdateWindow={updateWindow}
                                onAddWindow={addWindow}
                                onRemoveWindow={removeWindow}
                            />

                            <SpecialDaysSection
                                specialDays={specialDays}
                                isReadOnly={isReadOnly}
                                onUpdate={updateSpecialDay}
                                onToggle={toggleSpecialDay}
                                onDelete={deleteSpecialDay}
                                onAdd={addSpecialDay}
                            />
                        </>
                    )}

                    {!isReadOnly && (
                        <div className={classes.actions}>
                            <LoadingButton
                                type="button"
                                className={classes.saveButton}
                                variant="contained"
                                size="small"
                                loading={isSaving}
                                disabled={!hasUnsavedChanges}
                                onClick={applyChanges}
                                data-testid="save-tour-availability-button"
                            >
                                Save
                            </LoadingButton>
                        </div>
                    )}
                </FlexColumn>
            </form>
        );
    }
);

export default PropertyTouringHoursSection;
